// @MX:ANCHOR: [AUTO] 병원/기공소 단일 API 모듈 — SPEC-CHINA-ADMIN-HOSPITAL-001
// @MX:REASON: [AUTO] CompanyList.vue, CompanyFormModal.vue, AccountManageModal.vue 등 다수 화면이 이 모듈을 경유. fan_in >= 3
// @MX:SPEC: SPEC-CHINA-ADMIN-HOSPITAL-001

import { chinaInapi } from './chinaInapi';
import { parsePaginatedResponse, extractApiError } from './ecountEtl';

// @MX:NOTE: [AUTO] chinaInapi 인스턴스(Authorization Bearer + 401 refresh queue + Accept-Language)를 공유
// 신규 인터셉터 추가 없이 기존 인스턴스 재사용 — REQ-UBI-001

/**
 * 기본 URL prefix (type에 따라 달라짐)
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 */
function baseUrl(type) {
  return type === 'HOSPITAL' ? '/china/hospitals' : '/china/dental-labs';
}

// ============================================================
// 회사(병원/기공소) CRUD
// ============================================================

/**
 * 목록 조회 (페이지네이션, 필터, 정렬)
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {Object} params - { name, addrOne, medicalDept, registrationStatus, isTestCompany, page, limit, sortBy, sortOrder }
 * @returns {Promise<{ data, total, page, limit, totalPages }>}
 */
export function listCompanies(type, params = {}) {
  return chinaInapi.get(baseUrl(type), { params });
}

/**
 * 상세 조회
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} id
 */
export function getCompany(type, id) {
  return chinaInapi.get(`${baseUrl(type)}/${id}`);
}

/**
 * 생성 (mass-assignment whitelist는 백엔드 서비스 레이어에서 강제)
 * viewManufacturerIds/viewDentalLabIds/role/deleteYn/_id는 백엔드가 무시 — 전송 안 함(REQ-FORM-001)
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {Object} body
 */
export function createCompany(type, body) {
  return chinaInapi.post(baseUrl(type), body);
}

/**
 * 수정 (PATCH, 변경 필드만 전송 — REQ-FORM-002)
 * 운영자(XINLAIAN)만 viewManufacturerIds/viewDentalLabIds 전송 가능 (REQ-FORM-003)
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} id
 * @param {Object} body
 */
export function updateCompany(type, id, body) {
  return chinaInapi.patch(`${baseUrl(type)}/${id}`, body);
}

/**
 * 소프트 삭제 (운영자(XINLAIAN) 전용 — 백엔드가 403 강제)
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} id
 */
export function deleteCompany(type, id) {
  return chinaInapi.delete(`${baseUrl(type)}/${id}`);
}

/**
 * 기공소 생성 모달의 출고처 선택 패널용 — 행위자 회사 출고 기록의 고객 목록 조회.
 * SPEC-CHINA-COMPANY-OUTBOUND-PICKER-001 M1 (REQ-OP-010~018).
 * 파라미터 없음 — dealerCode 스코프는 서버가 행위자 회사 문서 기준으로 전권 판정(REQ-OP-011).
 * 응답: { mapped, customers: [{ customerCode, customerName, lastDeliveryDate, lineCount, registered }], total, truncated }
 */
export function getOutboundCustomers() {
  return chinaInapi.get('/china/dental-labs/outbound-customers');
}

// authz 편집용 회사 옵션 목록 (제조사/기공소 이름 선택, 운영자(XINLAIAN) 전용)
// type: 'MANUFACTURER' | 'DENTAL_LABORATORY'
export function getCompanyOptions(type) {
  return chinaInapi.get('/china/company-options', { params: { type } });
}

/**
 * 영업집조(营业执照) 이미지 업로드 (multipart/form-data) — SPEC-CHINA-COS-STORAGE-PROVIDER-001 M4
 * POST /china/company-assets/business-license
 * REQ-CS-040~044: 서버가 svg/pdf/비이미지(CS-FILE-001), 15MB 초과(CS-FILE-002),
 * 50MP·10000px 초과(CS-FILE-003)를 400으로 거부. 성공(201) 응답은 { url }만 포함.
 * REQ-CS-041: 서버 처리 타임아웃이 60초이므로, 공유 인스턴스 기본값(60000ms)보다 여유 있게
 * 요청 단위로 65000ms를 오버라이드한다 (인스턴스 기본값 자체는 변경하지 않음).
 * Content-Type은 지정하지 않는다 — 브라우저가 multipart boundary를 자동 설정.
 * @param {File} file - 업로드할 파일
 * @returns {Promise<{ data: { url: string } }>}
 */
export function uploadBusinessLicenseImage(file) {
  const formData = new FormData();
  formData.append('file', file);
  return chinaInapi.post('/china/company-assets/business-license', formData, {
    timeout: 65000,
  });
}

/**
 * 영업집조(营业执照) OCR 자동 채움 — SPEC-CHINA-BIZLICENSE-OCR-001 M3
 * POST /china/company-assets/business-license/ocr
 * 이미 업로드된 영업집조 이미지 URL을 서버가 OCR(이미지→텍스트) + LLM(텍스트→구조화)로
 * 처리해 "사람이 확인 후 선택 적용하는 제안값"을 반환한다(stateless — 회사 문서를 읽거나 쓰지 않음).
 * provider는 항상 서버 기본값(tencent)을 쓴다 — FE는 절대 전송하지 않음(UI에 선택지 없음).
 * REQ-CS-041과 동일한 근거로 65000ms 타임아웃을 오버라이드한다(OCR+LLM 처리는 업로드만큼 걸릴 수 있음).
 * @param {string} url - uploadBusinessLicenseImage가 반환한 이미지 URL
 * @returns {Promise<{ data: { provider: string, fields: Object, warnings: string[] } }>}
 */
export function extractBusinessLicenseOcr(url) {
  return chinaInapi.post(
    '/china/company-assets/business-license/ocr',
    { url },
    { timeout: 65000 },
  );
}

// ============================================================
// 계정 하위 관리
// ============================================================

/**
 * 회사 소속 계정 목록 조회
 * 자격증명 해시(password/refreshToken/initPwd)는 백엔드 sanitize로 미포함 — REQ-ACC-001
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} companyId
 */
export function listAccounts(type, companyId) {
  return chinaInapi.get(`${baseUrl(type)}/${companyId}/accounts`);
}

/**
 * 계정 생성 (초기 계정: username==password, isFirstLogin=Y — backend 책임)
 * 409 = 전역 username 중복 → UX 에러 표시 (REQ-ACC-002)
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} companyId
 * @param {{ username: string }} body
 */
export function createAccount(type, companyId, body) {
  return chinaInapi.post(`${baseUrl(type)}/${companyId}/accounts`, body);
}

/**
 * 계정 수정 (name만 허용 — OD-FE-4, REQ-ACC-003)
 * username/role/companyId/password는 편집 UI 미노출 + 전송 안 함
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} companyId
 * @param {string} accountId
 * @param {{ name: string }} body
 */
export function updateAccount(type, companyId, accountId, body) {
  return chinaInapi.patch(`${baseUrl(type)}/${companyId}/accounts/${accountId}`, body);
}

/**
 * 계정 삭제 — SPEC-CHINA-IAM-ACCOUNT-LIFECYCLE-001
 * 삭제 후 해당 계정은 재로그인할 수 없다(백엔드가 하드 삭제/차단 처리).
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} companyId
 * @param {string} accountId
 */
export function deleteAccount(type, companyId, accountId) {
  return chinaInapi.delete(`${baseUrl(type)}/${companyId}/accounts/${accountId}`);
}

/**
 * 계정 사용중지/재개 — SPEC-CHINA-IAM-ACCOUNT-LIFECYCLE-001
 * body.active=false면 사용중지(로그인 차단), true면 재개.
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} companyId
 * @param {string} accountId
 * @param {boolean} active
 */
export function setAccountStatus(type, companyId, accountId, active) {
  return chinaInapi.patch(`${baseUrl(type)}/${companyId}/accounts/${accountId}/status`, { active });
}

/**
 * 비밀번호 초기화 (isFirstLogin=Y 재설정은 backend 책임 — REQ-ACC-004)
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} companyId
 * @param {string} accountId
 * @param {{ newPassword: string }} body
 */
export function resetAccountPassword(type, companyId, accountId, body) {
  return chinaInapi.post(`${baseUrl(type)}/${companyId}/accounts/${accountId}/reset-password`, body);
}

// ============================================================
// 제조사 실무자(STAFF) 계정 관리 — SPEC-CHINA-MFR-STAFF-ACCOUNT-001
// @MX:ANCHOR: [AUTO] manufacturer/accounts API 래퍼 4종 진입점
// @MX:REASON: [AUTO] MfrStaffManage.vue에서 fan_in >= 3. 보안 게이트(BE AccessTokenGuard) 경유. 잘못 수정 시 실무자 계정 노출 위험.
// @MX:SPEC: SPEC-CHINA-MFR-STAFF-ACCOUNT-001
// ============================================================

/**
 * 제조사 소속 실무자/마스터 계정 목록 조회
 * MANUFACTURER 로그인 시 companyId 불필요(BE가 sub에서 추출).
 * 운영자(XINLAIAN)는 ?companyId= 지정 필요.
 * @param {Object} [params] - { companyId?: string }
 */
export function listManufacturerAccounts(params = {}) {
  return chinaInapi.get('/china/manufacturer/accounts', { params });
}

/**
 * 실무자 계정 생성 (username, name 필수 — ManufacturerAccountCreateDto)
 * BE가 초기 비밀번호를 username으로 설정하고 passwordChangeRequired=true 지정.
 * 409 = 전역 username 중복
 * @param {{ username: string, name: string }} body
 */
export function createStaffAccount(body) {
  return chinaInapi.post('/china/manufacturer/accounts', body);
}

/**
 * 제조사 계정 이름 수정 (name 필드만 — ManufacturerAccountUpdateDto)
 * @param {string} accountId - 계정 _id
 * @param {{ name: string }} body
 */
export function updateManufacturerAccount(accountId, body) {
  return chinaInapi.patch(`/china/manufacturer/accounts/${accountId}`, body);
}

/**
 * 제조사 계정 비밀번호 초기화
 * BE가 username으로 비밀번호 재설정 + passwordChangeRequired=true 지정.
 * @param {string} accountId - 계정 _id
 */
export function resetManufacturerAccountPassword(accountId) {
  return chinaInapi.post(`/china/manufacturer/accounts/${accountId}/reset-password`);
}

/**
 * 제조사 계정 사용중지/재개 — SPEC-CHINA-IAM-CONSOLE-001
 * [2026-08-04] 병원/기공소의 setAccountStatus와 동일 REST 관례(PATCH .../status)로 신규 추가.
 * IAM 콘솔 상세 ④보안 섹션이 제조사 계정에도 사용중지/재개를 요구하는데, 기존에는
 * hospitals/dental-labs 스코프에만 이 액션이 존재했다(비대칭). BE는 이 SPEC과 병행 구현 중.
 * @param {string} accountId - 계정 _id
 * @param {boolean} active
 */
export function setManufacturerAccountStatus(accountId, active) {
  return chinaInapi.patch(`/china/manufacturer/accounts/${accountId}/status`, { active });
}

/**
 * 제조사 계정 삭제 — SPEC-CHINA-IAM-CONSOLE-001
 * [2026-08-04] 병원/기공소의 deleteAccount와 동일 REST 관례로 신규 추가(사유는 위 setManufacturerAccountStatus 참조).
 * @param {string} accountId - 계정 _id
 */
export function deleteManufacturerAccount(accountId) {
  return chinaInapi.delete(`/china/manufacturer/accounts/${accountId}`);
}

// ============================================================
// 강제 로그아웃 — SPEC-CHINA-ACCTSEC-001
// ============================================================

/**
 * 병원/기공소 계정 강제 로그아웃 (tokenVersion 즉시 범프)
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} companyId
 * @param {string} accountId
 */
export function forceLogoutAccount(type, companyId, accountId) {
  return chinaInapi.post(`${baseUrl(type)}/${companyId}/accounts/${accountId}/force-logout`);
}

/**
 * 제조사 실무자 계정 강제 로그아웃
 * @param {string} accountId - 계정 _id
 */
export function forceLogoutManufacturerAccount(accountId) {
  return chinaInapi.post(`/china/manufacturer/accounts/${accountId}/force-logout`);
}

// ============================================================
// 로그인 잠금(F5) 해제 — 세션·비밀번호 무변경, failedLoginCount/lockedUntil만 초기화
// ============================================================

/**
 * 병원/기공소 계정 로그인 잠금 해제
 * @param {'HOSPITAL'|'DENTAL_LABORATORY'} type
 * @param {string} companyId
 * @param {string} accountId
 */
export function unlockAccount(type, companyId, accountId) {
  return chinaInapi.post(`${baseUrl(type)}/${companyId}/accounts/${accountId}/unlock`);
}

/**
 * 제조사 계정 로그인 잠금 해제
 * @param {string} accountId - 계정 _id
 */
export function unlockManufacturerAccount(accountId) {
  return chinaInapi.post(`/china/manufacturer/accounts/${accountId}/unlock`);
}

// ============================================================
// capability(비용 열람 등) 관리 — SPEC-CHINA-CAP-COST-001
// ============================================================

/**
 * 제조사 실무자 계정의 차단 capability 설정. **저장 형식은 deny-list(뺄셈)** 이다
 * (BE 판정은 allow-list — utils.js jwtCan() 주석 참조).
 * 마스터 전용. tokenVersion을 범프하므로 대상은 재로그인 후 반영.
 * @param {string} accountId - 계정 _id
 * @param {string[]} deniedCaps - 차단할 capability 배열 (예: ['certCard.cost'])
 */
export function setManufacturerAccountCapabilities(accountId, deniedCaps) {
  return chinaInapi.patch(`/china/manufacturer/accounts/${accountId}/capabilities`, { deniedCaps });
}

// ============================================================
// 헬퍼 재export (호출처 편의)
// ============================================================
export { parsePaginatedResponse, extractApiError };
